import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Send, Youtube } from 'lucide-react';

const Contact = () => {
  const locations = [
    {
      region: "Vest",
      person: "v/Jesper Olesen:",
      address: ["Torvet 1B", "9400 Nørresundby"],
      phone: "51 96 90 64",
      email: "jesper@finansoptimering.dk",
      mapUrl: "https://www.google.com/maps/search/?api=1&query=Torvet+1B,+9400+Nørresundby"
    },
    {
      region: "Midt",
      person: "v/Niels Rødsgaard:",
      address: ["Resenvej 85", "7800 Skive"],
      phone: "50 88 22 22",
      email: "niels@finansoptimering.dk",
      mapUrl: "https://www.google.com/maps/search/?api=1&query=Resenvej+85,+7800+Skive"
    },
    {
      region: "Øst",
      person: "v/Jesper Olesen:",
      address: ["Skættekæret 7", "2840 Holte"],
      phone: "51 96 90 64",
      email: "jesper@finansoptimering.dk",
      mapUrl: "https://www.google.com/maps/search/?api=1&query=Skættekæret+7,+2840+Holte"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        alert('Tak for din besked! Vi vender tilbage hurtigst muligt.');
        e.target.reset();
      } else {
        alert('Der opstod en fejl. Prøv venligst igen senere.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Der opstod en fejl. Prøv venligst igen senere.');
    }
  };

  return (
    <div className="py-16 bg-white">
      <Helmet>
        <title>Kontakt os: Få uvildig økonomisk rådgivning</title>
        <meta name="description" content="Kontakt os for økonomisk rådgivning til private og erhverv. Book et gratis og uforpligtende møde og lad os gennemgå din økonomi. Se mere her" />
      </Helmet>

      <div className="container mx-auto px-4 max-w-6xl">
        <nav className="text-sm text-slate-500 mb-8 flex items-center space-x-2">
          <Link to="/" className="hover:text-[#b60f00]">Forsiden</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">Kontakt os</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-12 break-words">Kontakt os</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {locations.map((loc, index) => (
            <div key={index} className="bg-slate-50 rounded-3xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{loc.region}</h2>
              <p className="text-[#b60f00] font-semibold mb-6">{loc.person}</p>
              
              <div className="space-y-4">
                <div className="text-slate-700">
                  {Array.isArray(loc.address) ? (
                    loc.address.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))
                  ) : (
                    loc.address
                  )}
                </div>
                <div>
                  <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="text-slate-700 hover:text-[#b60f00] transition-colors">
                    Tlf.: {loc.phone}
                  </a>
                </div>
                <div>
                  <a href={`mailto:${loc.email}`} className="text-slate-700 hover:text-[#b60f00] transition-colors break-all">
                    E-mail: {loc.email}
                  </a>
                </div>
              </div>
              
              {loc.mapUrl && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <a 
                    href={loc.mapUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm font-bold text-[#b60f00] hover:underline"
                  >
                    Find vej på kort
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="bg-slate-900 text-white rounded-3xl p-5 shadow-2xl">
            <h2 className="text-3xl font-bold mb-8">Kontakt os ved at udfylde formularen</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold mb-2">Dit navn (skal udfyldes)</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#b60f00] outline-none transition-all"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-2">Din e-mail (skal udfyldes)</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#b60f00] outline-none transition-all"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold mb-2">Dit telefon nummer</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#b60f00] outline-none transition-all"
                />
              </div>
              <div>
                <label htmlFor="department" className="block text-sm font-semibold mb-2">Hvilken afdeling vil du have fat i?</label>
                <select
                  id="department"
                  name="department"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#b60f00] outline-none transition-all appearance-none"
                >
                  <option value="København">København</option>
                  <option value="Aalborg">Aalborg</option>
                  <option value="Skive">Skive</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-semibold mb-2">Evt. besked</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#b60f00] outline-none transition-all resize-none"
                ></textarea>
              </div>

              {/* Honeypot field */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
              </div>

              <button
                type="submit"
                className="w-full bg-[#b60f00] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#a00d00] transition-all shadow-lg active:scale-95 flex items-center justify-center"
              >
                Send besked
              </button>
            </form>
          </div>

          <div className="space-y-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Få gratis tips på vores YouTube kanal</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Besøg vores YouTube kanal og få gratis tips og råd til din økonomi. Du kan med fordel abonnere på kanalen og få tilsendt en e-mail, hver gang vi opdaterer kanalen med nye videoer og indlæg.
              </p>
              <a 
                href="http://youtube.com/c/FinansoptimeringDK" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center bg-red-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-red-700 transition-all shadow-md active:scale-95"
              >
                <Youtube size={24} className="mr-3" />
                Finansoptimering på YouTube
              </a>
            </div>

            <div className="bg-red-50 rounded-3xl p-8 border border-red-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Uvildig rådgivning</h3>
              <p className="text-slate-600 leading-relaxed">
                Vi er 100% uafhængige af banker og realkreditinstitutter. Det betyder, at we udelukkende varetager dine interesser og finder de løsninger, der er bedst for din økonomi.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
